import React, { useState } from 'react';
import { CONTRACTS } from "src/constants/Steps";
import initialMetricsPublisher from '../metrics';
import SubmitDealershipFormButton from "src/components/SubmitDealershipFormButton";
import '../assets/contractsection.scss';
import { debug } from 'src/logger';
import {NEW_TAB} from "src/constants/Links";
import new_window from "src/images/icon/new_window.png";
import {getA2ZQuoteStatus} from "src/backend/GetA2ZQuoteStatusCaller";
import {getSafeguardContractStatus} from "src/backend/GetSafeguardContractStatusCaller";

interface ContractsSectionProps {
    a2zQuoteUrl: string;
    safeguardContractUrl: string;
    submitIntakeSuccess: boolean;
    a2zAssociationId: string;
    skipSafeguard?: boolean;
    intakeId: string;
    onSubmit?: () => void;
}

const ContractsSection: React.FC<ContractsSectionProps> = ({ a2zQuoteUrl, safeguardContractUrl, submitIntakeSuccess, a2zAssociationId, skipSafeguard = false, intakeId, onSubmit }) => {
    const [hasInteracted, setHasInteracted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const handleInteraction = () => {
        if (!hasInteracted) {
            const actionPublisher = initialMetricsPublisher.newChildActionPublisherForMethod('ContractSectionInteraction');
            actionPublisher.publishCounterMonitor('InteractedWithContractSectionCount', 1);
            setHasInteracted(true);
        }
    };

    const checkIfContractsAreSigned = async () => {
        if (skipSafeguard) {
            const a2zResponse = await getA2ZQuoteStatus({ a2zAssociationId: a2zAssociationId });
            console.log('A2Z contracts ' + (a2zResponse.signed ? 'have been signed.' : 'have not been signed.'));
            return a2zResponse.signed;
        }

        const [a2zResponse, safeguardResponse] = await Promise.all([
            getA2ZQuoteStatus({ a2zAssociationId: a2zAssociationId }),
            getSafeguardContractStatus({ contractTransactionId: intakeId }),
        ]);

        console.log('A2Z contracts ' + (a2zResponse.signed ? 'have been signed.' : 'have not been signed.'));
        console.log('Safeguard contracts ' + (safeguardResponse.signed ? 'have been signed.' : 'have not been signed.'));

        return a2zResponse.signed && safeguardResponse.signed;
    }

    const [consentConfirmed, setConsentConfirmed] = useState(false);

    const showContracts = a2zQuoteUrl && (skipSafeguard || safeguardContractUrl) && submitIntakeSuccess;
    debug('A2Z Quote URL:', a2zQuoteUrl, 'Safe-Guard URL:', safeguardContractUrl,
        'SubmitIntake Success:', submitIntakeSuccess, 'Show Contracts:', showContracts);

    return (
        <div className="section" id={CONTRACTS} onClick={handleInteraction}>
            {showContracts && <h1 className="page-heading">2. Review & sign contracts</h1>}
            {showContracts && <p className="page-description">When you are ready, review and sign contracts. You will need to return to this page after you complete the e-signing process.</p>}
            {!showContracts ? (
                <div className="error-message-container">
                    <h1 className="page-heading">Oops! We hit a snag with generating the A2Z Sync contract</h1>
                    <p className="page-description">While we figure out the problem, as a next step continue to register with Seller Central.</p>
                    <button className="submit-button" onClick={onSubmit}>Go to registration</button>
                </div>
            ) : (
                <div>
                    <div className="dealership-contract-info-box">
                        <div className="contracts-subsection">
                            <div className="contracts-subsection-header">A2Z Sync</div>
                            <p>We partnered with A2Z Sync to integrate your inventory and process orders with your dealership software.</p>
                            <button
                                type="button"
                                className="open-contract-new-tab-button"
                                onClick={() => {
                                    window.open(a2zQuoteUrl, NEW_TAB)
                                }}
                            >
                                Sign A2Z Sync contract
                                <img src={new_window} alt="" />
                            </button>
                        </div>

                        {!skipSafeguard && (
                            <div className="contracts-subsection">
                                <div className="contracts-subsection-header">Safe-Guard</div>
                                <p>Our service and protection products are provided and managed by Safe-Guard. That way, you can earn more money for every order you get through Amazon Autos.</p>
                                <button
                                    type="button"
                                    className="open-contract-new-tab-button"
                                    onClick={() => {
                                        window.open(safeguardContractUrl, NEW_TAB)
                                    }}
                                >
                                    Sign Safe-Guard contract
                                    <img src={new_window} alt="" />
                                </button>
                            </div>
                        )}
                    </div>

                </div>
            )}
            {onSubmit && showContracts && (
                <>
                    <div className="contracts-consent-section">
                        <SubmitDealershipFormButton
                            consentConfirmed={consentConfirmed}
                            onConsentChange={setConsentConfirmed}
                            onSubmit={(e: React.FormEvent) => {
                                e.preventDefault();
                                setIsSubmitting(true);
                                checkIfContractsAreSigned().then((signed) => {
                                    if (signed) {
                                        handleInteraction();
                                        onSubmit();
                                        setErrorMessage("")
                                    } else {
                                        setErrorMessage("Contracts don't appear to be signed yet, please try again.")
                                    }

                                    setIsSubmitting(false);
                                });
                            }}
                            isFormValid={consentConfirmed}
                            consentMessage="I confirm I have reviewed and signed the contracts above and give consent to Amazon Autos sharing these executed contracts with the respective third-party providers who are parties to these contracts for purposes of the Amazon Autos program, contract administration, and performance under these contracts."
                            warningMessage="Please note that upon submission of the contracts, you won't be able to go back to this page to make further modifications."
                            errorMessage={errorMessage}
                            isSubmitting={isSubmitting}
                            buttonText="Go to registration"
                            recordMetric={false}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default ContractsSection;
